import { useState } from "react";
import useSWR, { mutate } from "swr";
import { toast } from "sonner";
import { FolderPlus, Plus, Check, Layers } from "lucide-react";
import {
  addVideosToCollection,
  createCollection,
  listCollections,
} from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AddToCollectionDialog({ videoId, videoTitle }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const { data } = useSWR(open ? "/collections" : null, () => listCollections());

  const items = data?.items || [];

  const handleAdd = async (slug) => {
    setBusy(true);
    try {
      await addVideosToCollection(slug, [videoId]);
      toast.success("Saved to collection");
      await mutate("/collections");
      await mutate(`/collections/${slug}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save to collection");
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) {
      toast.error("Give your collection a name");
      return;
    }
    setBusy(true);
    try {
      const c = await createCollection({
        title: newTitle,
        video_ids: [videoId],
      });
      toast.success(`Created "${c.title}"`);
      setNewTitle("");
      setCreating(false);
      setOpen(false);
      await mutate("/collections");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create collection");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="save-to-collection-btn"
          variant="outline"
          className="rounded-full bg-transparent border-white/15 hover:border-[#d95d39] hover:text-[#d95d39] hover:bg-transparent font-mono-tech text-[11px] uppercase tracking-[0.22em]"
        >
          <FolderPlus className="w-4 h-4 mr-2" /> Save
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#12141d] border-white/10 text-[#f4f1ea] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-editorial text-2xl">
            Save to collection
          </DialogTitle>
        </DialogHeader>
        <div className="mono-label text-white/40 -mt-2 line-clamp-1">{videoTitle}</div>

        <div className="max-h-72 overflow-y-auto custom-scrollbar pr-1 mt-2 space-y-1">
          {items.length === 0 ? (
            <div className="text-white/50 text-sm py-4">
              You have no collections yet. Create one below.
            </div>
          ) : (
            items.map((c) => {
              const already = (c.video_ids || []).includes?.(videoId);
              return (
                <button
                  key={c.slug}
                  onClick={() => handleAdd(c.slug)}
                  disabled={busy}
                  data-testid={`pick-collection-${c.slug}`}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-white/10 hover:border-[#d95d39]/60 hover:bg-white/[0.03] transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Layers className="w-4 h-4 text-[#d95d39] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-[#f4f1ea] truncate">{c.title}</div>
                      <div className="mono-label text-white/40">{c.count} videos</div>
                    </div>
                  </div>
                  {already ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <Plus className="w-4 h-4 text-white/40" />
                  )}
                </button>
              );
            })
          )}
        </div>

        {creating ? (
          <div className="pt-2 space-y-3 border-t border-white/10">
            <label className="mono-label text-white/50">New collection</label>
            <Input
              autoFocus
              data-testid="quick-create-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="e.g. PC Benchmarks 2024"
              className="bg-[#0c0e15] border-white/10 focus-visible:ring-[#d95d39]"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setCreating(false)}
                className="text-white/60 hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={busy}
                data-testid="quick-create-submit"
                className="rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-[11px] uppercase tracking-[0.22em]"
              >
                <Plus className="w-4 h-4 mr-2" /> Create & save
              </Button>
            </div>
          </div>
        ) : (
          <DialogFooter>
            <Button
              onClick={() => setCreating(true)}
              variant="outline"
              data-testid="show-quick-create"
              className="w-full rounded-full bg-transparent border-white/15 hover:border-[#d95d39] hover:text-[#d95d39] hover:bg-transparent font-mono-tech text-[11px] uppercase tracking-[0.22em]"
            >
              <FolderPlus className="w-4 h-4 mr-2" /> New collection
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
